/**
 * The committee card payload — GET /api/campaign-finance/committees/:co_id/brief
 * (and /committees/brief?name=). Citizen Person Page & Committee Card PRD §9.
 */
export type CommitteeStatus = 'active' | 'dormant' | 'closed';

export interface CommitteeBriefMeasure {
  id: string;
  number: string | null;
  title: string | null;
  side: 'support' | 'oppose';
  election_year?: number | null;
}

export interface CommitteeBriefDonor {
  name: string;
  city: string | null;
  amount: number;
}

export interface CommitteeBrief {
  co_id: string;
  name: string;
  type: string | null;
  status: CommitteeStatus;
  closed_on: string | null;
  registered_on: string | null;
  purpose: string | null;
  candidate: { person_id: string; name: string | null; office: string | null } | null;
  cycle: string | null;
  as_of: string | null;
  totals: { raised: number; spent: number; cash_on_hand: number };
  measures: CommitteeBriefMeasure[];
  registered_agent: string | null;
  top_donors: CommitteeBriefDonor[];
  tracer_url: string;
}

/** Which app the card is open in — decides where person and measure links go. */
export type CommitteeApp = 'citizen' | 'public' | 'pro';

export function appFromPath(pathname: string): CommitteeApp {
  if (pathname.startsWith('/citizen')) return 'citizen';
  if (pathname.startsWith('/public')) return 'public';
  return 'pro';
}

/** The person page for the committee's candidate, in whichever app is open. */
export function personHref(app: CommitteeApp, personId: string): string {
  const id = encodeURIComponent(personId);
  if (app === 'citizen') return `/citizen/candidate/${id}`;
  if (app === 'public') return `/public/candidates/${id}`;
  return `/candidates/${id}`;
}

/** The measure page (Pro, public) or the measure's card on the citizen ballot. */
export function measureHref(app: CommitteeApp, measureId: string): string {
  const id = encodeURIComponent(measureId);
  if (app === 'citizen') return `/citizen/ballot#measure-${id}`;
  if (app === 'public') return `/public/ballot-initiatives/${id}`;
  return `/ballot-initiatives/${id}`;
}

/** TRACER committee search — the fallback when a name can't be matched. */
export const TRACER_COMMITTEE_SEARCH_URL =
  'https://tracer.sos.colorado.gov/PublicSite/SearchPages/CommitteeSearch.aspx';

/** Chip label for TRACER's committee type ("Candidate Committee" → "Candidate committee"). */
export function committeeTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  const t = type.trim().toLowerCase();
  if (!t) return null;
  if (t.includes('candidate')) return 'Candidate committee';
  if (t.includes('issue')) return 'Issue committee';
  if (t.includes('small donor')) return 'Small donor committee';
  if (t.includes('independent expenditure')) return 'Independent expenditure committee';
  if (t.includes('political')) return 'Political committee';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function isCandidateCommittee(brief: Pick<CommitteeBrief, 'type' | 'candidate'>): boolean {
  return Boolean(brief.candidate) || /candidate/i.test(brief.type || '');
}
